import React, { useState, useEffect } from "react";
import {
  Grid,
  Card,
  CardActionArea,
  CardContent,
  Typography,
  Box,
  useMediaQuery,
  useTheme,
  Paper,
  LinearProgress,
  Button,
  Chip,
  alpha,
  CircularProgress,
  Divider,
} from "@mui/material";
import {
  People,
  Assignment,
  Image,
  School,
  CheckCircle,
  BarChart,
  Info,
  CameraAlt,
  TrendingUp,
  Biotech,
  DataUsage,
  ShowChart,
  BubbleChart,
  ArrowForwardIos,
  Assessment,
  Timeline,
  Warning,
} from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import { getDashboardStats, DashboardStats } from "../../app/services/dashboardApi";

interface DashboardItem {
  title: string;
  description: string;
  icon: React.ReactNode;
  path: string;
}

const dashboardItems: DashboardItem[] = [
  {
    title: "Patients",
    description: "Manage patient profiles and histories",
    icon: <People fontSize="large" />,
    path: "/patients",
  },
  {
    title: "Cases",
    description: "View and create clinical cases",
    icon: <Assignment fontSize="large" />,
    path: "/cases",
  },
  // {
  //   title: "Images",
  //   description: "Upload and review medical images",
  //   icon: <Image fontSize="large" />,
  //   path: "/images",
  // },
  // {
  //   title: "Onboarding",
  //   description: "Customize workflows and preferences",
  //   icon: <School fontSize="large" />,
  //   path: "/onboarding",
  // },
  
  // {
  //   title: "Analytics",
  //   description: "Track patient and case statistics",
  //   icon: <BarChart fontSize="large" />,
  //   path: "/analytics",
  // },
  // {
  //   title: "Knowledge Hub",
  //   description: "Guidelines, AI insights, and references",
  //   icon: <Info fontSize="large" />,
  //   path: "/knowledge",
  // },
];

export default function Dashboard() {
  const navigate = useNavigate();
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === 'dark';
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("sm"));
  const [scanActive, setScanActive] = React.useState(false);
  const [scanProgress, setScanProgress] = useState(0);
  const [dashboardStats, setDashboardStats] = useState<DashboardStats | null>(null);
  const [statsLoading, setStatsLoading] = useState(true);

  // Load dashboard statistics
  useEffect(() => {
    const loadStats = async () => {
      try {
        setStatsLoading(true);
        const stats = await getDashboardStats();
        setDashboardStats(stats);
      } catch (error) {
        console.error('Failed to load dashboard stats:', error);
      } finally {
        setStatsLoading(false);
      }
    };

    loadStats();
  }, []);

  // Simulated scan progress
  useEffect(() => {
    if (scanActive) {
      const interval = setInterval(() => {
        setScanProgress((prev) => {
          if (prev >= 100) {
            setScanActive(false);
            return 0;
          }
          return prev + 5;
        });
      }, 150);
      return () => clearInterval(interval);
    }
  }, [scanActive]);

  // Navigate to ChatDomain after scan completes
  useEffect(() => {
    if (scanProgress === 100 && scanActive) {
      setTimeout(() => {
        navigate('/ChatDomain');
      }, 500); // slight delay for UX
    }
  }, [scanProgress, scanActive, navigate]);

  // Simulated AI stats
  const aiStats = {
    diagnosesAssisted: 5000,
    accuracyRate: 89,
    averageTime: "2.3 min",
    recentInsights: ["Melanoma detection improved by 12%", "New pattern recognition for psoriasis","AI-assisted triage reduced time by 30%"],
  };

  // Simulated epidemiology stats
  const epidemiologyStats = {
    activeCases: 12843,
    trendsIdentified: 18,
    regionsMonitored: 27,
    lastUpdated: "2 hours ago"
  };

  // Recent research topics
  const recentResearch = [
    { title: "Noetiv Skin Engine", tag: "AI" , link: "https://www.sciencedirect.com/science/article/pii/S1386505625001157"},
    { title: "Augmented Health Applications", tag: "Process", link:"https://www.mdpi.com/2079-9292/13/2/320" },
    { title: "Optimization", tag: "ML" ,link:"https://www.sciencedirect.com/science/article/pii/S0950705125010202"}
  ];

  // Animation states for under construction components
  const [doctorProgress, setDoctorProgress] = useState(0);
  const [epiProgress, setEpiProgress] = useState(0);

  // Under construction animations
  useEffect(() => {
    const doctorTimer = setInterval(() => {
      setDoctorProgress((prevProgress) => 
        prevProgress >= 100 ? 0 : prevProgress + 1
      );
    }, 800);
    
    const epiTimer = setInterval(() => {
      setEpiProgress((prevProgress) => 
        prevProgress >= 100 ? 0 : prevProgress + 1
      );
    }, 600);
    
    return () => {
      clearInterval(doctorTimer);
      clearInterval(epiTimer);
    };
  }, []);

  return (
    <Box sx={{ 
      flexGrow: 1, 
      p: { xs: 1, sm: 2, md: 3 },
      backgroundImage: isDarkMode 
        ? 'radial-gradient(circle, rgba(35,35,40,1) 0%, rgba(28,28,33,1) 100%)'
        : 'radial-gradient(circle, rgba(255,255,255,1) 0%, rgba(240,249,255,1) 100%)',
      position: 'relative',
      overflow: 'hidden',
    }}>
      {/* Decorative DNA helix pattern */}
      <Box sx={{
        position: 'absolute',
        top: 0,
        right: 0,
        bottom: 0,
        left: 0,
        opacity: isDarkMode ? 0.07 : 0.04,
        backgroundImage: isDarkMode
          ? 'url("data:image/svg+xml,%3Csvg width="100" height="100" viewBox="0 0 100 100" xmlns="http://www.w3.org/2000/svg"%3E%3Cpath d="M11 18c3.866 0 7-3.134 7-7s-3.134-7-7-7-7 3.134-7 7 3.134 7 7 7zm48 25c3.866 0 7-3.134 7-7s-3.134-7-7-7-7 3.134-7 7 3.134 7 7 7zm-43-7c1.657 0 3-1.343 3-3s-1.343-3-3-3-3 1.343-3 3 1.343 3 3 3zm63 31c1.657 0 3-1.343 3-3s-1.343-3-3-3-3 1.343-3 3 1.343 3 3 3zM34 90c1.657 0 3-1.343 3-3s-1.343-3-3-3-3 1.343-3 3 1.343 3 3 3zm56-76c1.657 0 3-1.343 3-3s-1.343-3-3-3-3 1.343-3 3 1.343 3 3 3zM12 86c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm28-65c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm23-11c2.76 0 5-2.24 5-5s-2.24-5-5-5-5 2.24-5 5 2.24 5 5 5zm-6 60c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm29 22c2.76 0 5-2.24 5-5s-2.24-5-5-5-5 2.24-5 5 2.24 5 5 5zM32 63c2.76 0 5-2.24 5-5s-2.24-5-5-5-5 2.24-5 5 2.24 5 5 5zm57-13c2.76 0 5-2.24 5-5s-2.24-5-5-5-5 2.24-5 5 2.24 5 5 5zm-9-21c1.105 0 2-.895 2-2s-.895-2-2-2-2 .895-2 2 .895 2 2 2zM60 91c1.105 0 2-.895 2-2s-.895-2-2-2-2 .895-2 2 .895 2 2 2zM35 41c1.105 0 2-.895 2-2s-.895-2-2-2-2 .895-2 2 .895 2 2 2zM12 60c1.105 0 2-.895 2-2s-.895-2-2-2-2 .895-2 2 .895 2 2 2z" fill="%23ffffff" fill-opacity="0.5" fill-rule="evenodd"/%3E%3C/svg%3E")'
          : 'url("data:image/svg+xml,%3Csvg width="100" height="100" viewBox="0 0 100 100" xmlns="http://www.w3.org/2000/svg"%3E%3Cpath d="M11 18c3.866 0 7-3.134 7-7s-3.134-7-7-7-7 3.134-7 7 3.134 7 7 7zm48 25c3.866 0 7-3.134 7-7s-3.134-7-7-7-7 3.134-7 7 3.134 7 7 7zm-43-7c1.657 0 3-1.343 3-3s-1.343-3-3-3-3 1.343-3 3 1.343 3 3 3zm63 31c1.657 0 3-1.343 3-3s-1.343-3-3-3-3 1.343-3 3 1.343 3 3 3zM34 90c1.657 0 3-1.343 3-3s-1.343-3-3-3-3 1.343-3 3 1.343 3 3 3zm56-76c1.657 0 3-1.343 3-3s-1.343-3-3-3-3 1.343-3 3 1.343 3 3 3zM12 86c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm28-65c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm23-11c2.76 0 5-2.24 5-5s-2.24-5-5-5-5 2.24-5 5 2.24 5 5 5zm-6 60c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm29 22c2.76 0 5-2.24 5-5s-2.24-5-5-5-5 2.24-5 5 2.24 5 5 5zM32 63c2.76 0 5-2.24 5-5s-2.24-5-5-5-5 2.24-5 5 2.24 5 5 5zm57-13c2.76 0 5-2.24 5-5s-2.24-5-5-5-5 2.24-5 5 2.24 5 5 5zm-9-21c1.105 0 2-.895 2-2s-.895-2-2-2-2 .895-2 2 .895 2 2 2zM60 91c1.105 0 2-.895 2-2s-.895-2-2-2-2 .895-2 2 .895 2 2 2zM35 41c1.105 0 2-.895 2-2s-.895-2-2-2-2 .895-2 2 .895 2 2 2zM12 60c1.105 0 2-.895 2-2s-.895-2-2-2-2 .895-2 2 .895 2 2 2z" fill="%2303a9f4" fill-opacity="0.5" fill-rule="evenodd"/%3E%3C/svg%3E")',
        backgroundSize: '30px 30px',
        zIndex: -1,
      }} />

      {/* Usage Statistics Section */}
      <Paper elevation={4} sx={{ 
        mb: 3, 
        p: { xs: 1.5, sm: 2, md: 3 },
        background: isDarkMode
          ? 'linear-gradient(120deg, rgba(40,30,60,1) 0%, rgba(30,25,50,1) 100%)'
          : 'linear-gradient(120deg, rgba(250,245,255,1) 0%, rgba(245,240,255,1) 100%)',
        borderRadius: 2,
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <DataUsage color="primary" sx={{ mr: 1 }} />
          <Typography variant="h6" fontWeight="medium">Your Activity Overview</Typography>
        </Box>
        
        {statsLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <Grid container spacing={2}>
            {/* Main Statistics Cards */}
            <Grid item xs={6} sm={3}>
              <Card elevation={2} sx={{ 
                p: 2, 
                textAlign: 'center',
                background: isDarkMode 
                  ? alpha(theme.palette.background.paper, 0.8) 
                  : alpha(theme.palette.common.white, 0.9)
              }}>
                <Typography variant="h4" color="primary" fontWeight="bold">
                  {dashboardStats?.userStats.totalMappingSessions || 0}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Mapping Sessions
                </Typography>
              </Card>
            </Grid>
            
            <Grid item xs={6} sm={3}>
              <Card elevation={2} sx={{ 
                p: 2, 
                textAlign: 'center',
                background: isDarkMode 
                  ? alpha(theme.palette.background.paper, 0.8) 
                  : alpha(theme.palette.common.white, 0.9)
              }}>
                <Typography variant="h4" color="secondary" fontWeight="bold">
                  {dashboardStats?.userStats.totalAnnotations || 0}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Annotations
                </Typography>
              </Card>
            </Grid>
            
            <Grid item xs={6} sm={3}>
              <Card elevation={2} sx={{ 
                p: 2, 
                textAlign: 'center',
                background: isDarkMode 
                  ? alpha(theme.palette.background.paper, 0.8) 
                  : alpha(theme.palette.common.white, 0.9)
              }}>
                <Typography variant="h4" color="info.main" fontWeight="bold">
                  {dashboardStats?.userStats.totalABCDEAssessments || 0}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  ABCDE Assessments
                </Typography>
              </Card>
            </Grid>
            
            <Grid item xs={6} sm={3}>
              <Card elevation={2} sx={{ 
                p: 2, 
                textAlign: 'center',
                background: isDarkMode 
                  ? alpha(theme.palette.background.paper, 0.8) 
                  : alpha(theme.palette.common.white, 0.9)
              }}>
                <Typography variant="h4" color="success.main" fontWeight="bold">
                  {dashboardStats?.userStats.patientsCount || 0}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Patients
                </Typography>
              </Card>
            </Grid>

            {/* Additional Metrics */}
            <Grid item xs={12} sm={6}>
              <Box sx={{ 
                p: 2, 
                borderRadius: 2, 
                bgcolor: isDarkMode ? alpha(theme.palette.background.paper, 0.15) : alpha(theme.palette.common.white, 0.5) 
              }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <Assessment color="primary" sx={{ mr: 1 }} />
                  <Typography variant="subtitle1" fontWeight="medium">
                    Average ABCDE Score
                  </Typography>
                </Box>
                <Typography variant="h5" color="primary">
                  {dashboardStats?.userStats.averageAssessmentScore?.toFixed(1) || '0.0'}/5
                </Typography>
                <LinearProgress 
                  variant="determinate" 
                  value={(dashboardStats?.userStats.averageAssessmentScore || 0) * 20} 
                  sx={{ mt: 1, height: 8, borderRadius: 4 }}
                />
              </Box>
            </Grid>

            <Grid item xs={12} sm={6}>
              <Box sx={{ 
                p: 2, 
                borderRadius: 2, 
                bgcolor: isDarkMode ? alpha(theme.palette.background.paper, 0.15) : alpha(theme.palette.common.white, 0.5) 
              }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <Warning color="warning" sx={{ mr: 1 }} />
                  <Typography variant="subtitle1" fontWeight="medium">
                    High Risk Findings
                  </Typography>
                </Box>
                <Typography variant="h5" color="warning.main">
                  {dashboardStats?.userStats.highRiskFindings || 0}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                  Requiring attention
                </Typography>
              </Box>
            </Grid>

            {/* Weekly Progress Chart */}
            {dashboardStats?.weeklyProgress && dashboardStats.weeklyProgress.length > 0 && (
              <Grid item xs={12}>
                <Divider sx={{ my: 2 }} />
                <Box sx={{ mb: 2 }}>
                  <Typography variant="subtitle1" fontWeight="medium" sx={{ display: 'flex', alignItems: 'center' }}>
                    <Timeline sx={{ mr: 1 }} />
                    Weekly Progress
                  </Typography>
                </Box>
                <Grid container spacing={1}>
                  {dashboardStats.weeklyProgress.map((week, index) => (
                    <Grid item xs={3} key={index}>
                      <Box sx={{ 
                        p: 1.5, 
                        textAlign: 'center',
                        borderRadius: 2,
                        bgcolor: isDarkMode ? alpha(theme.palette.background.paper, 0.1) : alpha(theme.palette.common.white, 0.7)
                      }}>
                        <Typography variant="caption" color="text.secondary">
                          {week.week}
                        </Typography>
                        <Typography variant="body2" fontWeight="medium">
                          {week.mappingSessions} sessions
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {week.annotations} annotations
                        </Typography>
                      </Box>
                    </Grid>
                  ))}
                </Grid>
              </Grid>
            )}
          </Grid>
        )}
      </Paper>

      {/* Quick Start - Patient-First Workflow */}
      <Paper elevation={4} sx={{ 
        mb: 3, 
        p: { xs: 2, sm: 3, md: 4 },
        background: isDarkMode
          ? 'linear-gradient(135deg, rgba(30,80,130,1) 0%, rgba(20,60,110,1) 50%, rgba(25,70,120,1) 100%)'
          : 'linear-gradient(135deg, rgba(25,118,210,1) 0%, rgba(30,136,229,1) 50%, rgba(13,101,193,1) 100%)',
        borderRadius: 3,
        position: 'relative',
        overflow: 'hidden',
        color: 'white'
      }}>
        {/* Background pattern */}
        <Box sx={{
          position: 'absolute',
          top: 0,
          right: 0,
          bottom: 0,
          left: 0,
          opacity: 0.1,
          backgroundImage: 'url("data:image/svg+xml,%3Csvg width="60" height="60" viewBox="0 0 60 60" xmlns="http://www.w3.org/2000/svg"%3E%3Cg fill="none" fill-rule="evenodd"%3E%3Cg fill="%23ffffff" fill-opacity="0.4"%3E%3Ccircle cx="30" cy="30" r="4"/%3E%3Ccircle cx="30" cy="10" r="2"/%3E%3Ccircle cx="30" cy="50" r="2"/%3E%3Ccircle cx="10" cy="30" r="2"/%3E%3Ccircle cx="50" cy="30" r="2"/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")',
          backgroundSize: '40px 40px',
          zIndex: 0,
        }} />
        
        <Box sx={{ 
          display: 'flex', 
          flexDirection: {xs: 'column', lg: 'row'},
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 3,
          position: 'relative',
          zIndex: 1
        }}>
          {/* Content Section */}
          <Box sx={{ flex: 1, textAlign: { xs: 'center', lg: 'left' } }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2, justifyContent: { xs: 'center', lg: 'flex-start' } }}>
              <Box sx={{ 
                bgcolor: alpha('#ffffff', 0.2),
                borderRadius: '50%',
                p: 2,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                border: '2px solid rgba(255,255,255,0.3)',
                boxShadow: '0 8px 32px rgba(0,0,0,0.1)'
              }}>
                <People sx={{ color: 'white', fontSize: { xs: '2rem', md: '2.5rem' } }} />
              </Box>
              <Box>
                <Typography variant={isSmallScreen ? "h5" : "h4"} fontWeight="bold" sx={{ 
                  mb: 0.5,
                  background: 'linear-gradient(45deg, #ffffff 30%, rgba(255,255,255,0.8) 90%)',
                  backgroundClip: 'text',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent'
                }}>
                  Start Your Journey
                </Typography>
                <Typography variant="subtitle1" sx={{ 
                  color: alpha('#ffffff', 0.9),
                  fontWeight: 500
                }}>
                  Patient-First Workflow
                </Typography>
              </Box>
            </Box>
            
            <Typography variant="body1" sx={{ 
              mb: 3, 
              color: alpha('#ffffff', 0.85),
              fontSize: { xs: '1rem', md: '1.1rem' },
              lineHeight: 1.6,
              maxWidth: { lg: '500px' }
            }}>
              Begin your clinical assessment by creating or selecting a patient profile. 
              Our streamlined workflow guides you through comprehensive mole mapping, 
              dermatoscopic analysis, and AI-powered ABCDE assessment.
            </Typography>

            {/* Feature highlights */}
            <Box sx={{ 
              display: 'flex', 
              flexDirection: { xs: 'column', sm: 'row' },
              gap: 2, 
              mb: 3,
              justifyContent: { xs: 'center', lg: 'flex-start' }
            }}>
              {[
                { icon: <People />, text: "Patient Profiles" },
                { icon: <CameraAlt />, text: "Image Capture" },
                { icon: <Biotech />, text: "AI Analysis" }
              ].map((feature, index) => (
                <Box key={index} sx={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  gap: 1,
                  bgcolor: alpha('#ffffff', 0.15),
                  borderRadius: 2,
                  px: 2,
                  py: 1,
                  border: '1px solid rgba(255,255,255,0.2)'
                }}>
                  {React.cloneElement(feature.icon, { 
                    sx: { fontSize: '1.2rem', color: alpha('#ffffff', 0.9) } 
                  })}
                  <Typography variant="body2" sx={{ 
                    color: alpha('#ffffff', 0.9),
                    fontWeight: 500
                  }}>
                    {feature.text}
                  </Typography>
                </Box>
              ))}
            </Box>
          </Box>

          {/* Action Section */}
          <Box sx={{ 
            display: 'flex', 
            flexDirection: 'column',
            alignItems: 'center',
            gap: 2,
            minWidth: { lg: '280px' }
          }}>
            {scanActive ? (
              <Box sx={{ width: '100%', textAlign: 'center' }}>
                <CircularProgress 
                  size={80} 
                  thickness={4}
                  sx={{ 
                    color: 'white',
                    mb: 2,
                    filter: 'drop-shadow(0 4px 8px rgba(0,0,0,0.3))'
                  }} 
                />
                <Typography variant="h6" sx={{ mb: 1, color: 'white' }}>
                  Redirecting to Patients...
                </Typography>
                <Typography variant="body2" sx={{ color: alpha('#ffffff', 0.8) }}>
                  Setting up your clinical workspace
                </Typography>
              </Box>
            ) : (
              <>
                <Button
                  variant="contained"
                  size="large"
                  startIcon={<People />}
                  onClick={() => {
                    setScanActive(true);
                    setTimeout(() => {
                      setScanActive(false);
                      navigate('/patients');
                    }, 2000);
                  }}
                  sx={{ 
                    px: 4, 
                    py: 1.5,
                    fontSize: '1.1rem',
                    fontWeight: 'bold',
                    background: 'rgba(255,255,255,0.95)',
                    color: theme.palette.primary.main,
                    border: '2px solid rgba(255,255,255,0.3)',
                    borderRadius: 3,
                    boxShadow: '0 8px 32px rgba(0,0,0,0.2)',
                    backdropFilter: 'blur(10px)',
                    '&:hover': {
                      background: 'rgba(255,255,255,1)',
                      transform: 'translateY(-2px)',
                      boxShadow: '0 12px 40px rgba(0,0,0,0.3)',
                    },
                    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                    minWidth: '220px'
                  }}
                >
                  Begin with Patients
                </Button>
                
                <Typography variant="body2" sx={{ 
                  color: alpha('#ffffff', 0.7),
                  textAlign: 'center',
                  fontSize: '0.9rem'
                }}>
                  {/* Or jump to{' '}
                  <Box 
                    component="span" 
                    onClick={() => navigate('/cases')}
                    sx={{ 
                      color: 'white',
                      textDecoration: 'underline',
                      cursor: 'pointer',
                      fontWeight: 500,
                      '&:hover': { color: alpha('#ffffff', 0.8) }
                    }}
                  >
                    existing cases
                  </Box> */}
                </Typography>
              </>
            )}
          </Box>
        </Box>

        {/* Floating elements for visual appeal */}
        <Box sx={{
          position: 'absolute',
          top: '20%',
          right: '10%',
          width: '80px',
          height: '80px',
          borderRadius: '50%',
          background: 'radial-gradient(circle, rgba(255,255,255,0.1) 0%, rgba(255,255,255,0.05) 100%)',
          border: '1px solid rgba(255,255,255,0.1)',
          animation: 'float 6s ease-in-out infinite',
          '@keyframes float': {
            '0%, 100%': { transform: 'translateY(0px) rotate(0deg)' },
            '50%': { transform: 'translateY(-20px) rotate(180deg)' },
          },
          display: { xs: 'none', md: 'block' }
        }} />
        
        <Box sx={{
          position: 'absolute',
          bottom: '15%',
          left: '8%',
          width: '60px',
          height: '60px',
          borderRadius: '50%',
          background: 'radial-gradient(circle, rgba(255,255,255,0.08) 0%, rgba(255,255,255,0.03) 100%)',
          border: '1px solid rgba(255,255,255,0.08)',
          animation: 'float 8s ease-in-out infinite reverse',
          display: { xs: 'none', md: 'block' }
        }} />
      </Paper>

      {/* Analytics Panels - Now Two Side by Side - Under Construction Style */}
      <Grid container spacing={{ xs: 2, md: 3 }} sx={{ mb: 3 }}>
        {/* Doctor Onboarding Panel */}
        <Grid item xs={12} md={6}>
          <Paper elevation={3} sx={{ 
            p: { xs: 2, sm: 2.5, md: 3 }, 
            height: '100%',
            background: isDarkMode
              ? 'linear-gradient(120deg, rgba(45,45,70,1) 0%, rgba(35,40,65,1) 100%)'
              : 'linear-gradient(120deg, rgba(240,240,255,1) 0%, rgba(230,242,255,1) 100%)',
            borderRadius: 2,
            position: 'relative',
            overflow: 'hidden'
          }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
              <Box>
                <Typography variant="h6" fontWeight="medium">Doctor Onboarding</Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                  Personalized workflow setup for medical professionals
                </Typography>
              </Box>
              <Chip 
                label="Coming Soon" 
                color="primary" 
                size="small"
                sx={{ 
                  borderRadius: 1, 
                  fontWeight: 'medium',
                  background: `linear-gradient(45deg, ${theme.palette.primary.main} 30%, ${alpha(theme.palette.primary.main, 0.8)} 90%)`
                }}
              />
            </Box>

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 3, mt: 3 }}>
              <Box position="relative" display="inline-flex">
                <CircularProgress 
                  variant="determinate" 
                  value={doctorProgress} 
                  size={80}
                  thickness={4}
                  sx={{ color: theme.palette.primary.main }}
                />
                <Box
                  sx={{
                    top: 0,
                    left: 0,
                    bottom: 0,
                    right: 0,
                    position: 'absolute',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.9rem' }}>
                    {`${Math.round(doctorProgress)}%`}
                  </Typography>
                </Box>
              </Box>
              
              <Box sx={{ flex: 1 }}>
                <Typography variant="body2" paragraph>
                  Build your personalized clinical workflow, setup specialty preferences, and configure AI-assistance levels.
                </Typography>
                <Button 
                  variant="outlined" 
                  color="primary" 
                  size="small" 
                  endIcon={<ArrowForwardIos />}
                  onClick={() => navigate('/DoctorOnboarding')}
                  sx={{ mt: 1 }}
                >
                  View Details
                </Button>
              </Box>
            </Box>
          </Paper>
        </Grid>
        
        {/* Epidemiology Analytics Panel */}
        <Grid item xs={12} md={6}>
          <Paper elevation={3} sx={{ 
            p: { xs: 2, sm: 2.5, md: 3 },
            height: '100%',
            background: isDarkMode
              ? 'linear-gradient(120deg, rgba(40,50,65,1) 0%, rgba(30,45,70,1) 100%)'
              : 'linear-gradient(120deg, rgba(235,250,255,1) 0%, rgba(225,245,255,1) 100%)',
            borderRadius: 2,
            position: 'relative'
          }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
              <Box>
                <Typography variant="h6" fontWeight="medium">Epidemiology Analytics</Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                  Disease evolution and population health insights
                </Typography>
              </Box>
              <Chip 
                label="Coming Soon" 
                color="secondary"
                size="small" 
                sx={{ 
                  borderRadius: 1,
                  fontWeight: 'medium',
                  background: `linear-gradient(45deg, ${theme.palette.secondary.main} 30%, ${alpha(theme.palette.secondary.main, 0.8)} 90%)`
                }}
              />
            </Box>

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 3, mt: 3 }}>
              <Box position="relative" display="inline-flex">
                <CircularProgress 
                  variant="determinate" 
                  value={epiProgress} 
                  size={80}
                  thickness={4}
                  sx={{ color: theme.palette.secondary.main }}
                />
                <Box
                  sx={{
                    top: 0,
                    left: 0,
                    bottom: 0,
                    right: 0,
                    position: 'absolute',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.9rem' }}>
                    {`${Math.round(epiProgress)}%`}
                  </Typography>
                </Box>
              </Box>
              
              <Box sx={{ flex: 1 }}>
                <Typography variant="body2" paragraph>
                  Advanced analytics for tracking disease patterns, geographical distribution, and treatment efficacy across populations.
                </Typography>
                <Button 
                  variant="outlined" 
                  color="secondary" 
                  size="small" 
                  endIcon={<ArrowForwardIos />}
                  onClick={() => navigate('/EpidemiologyAnalytics')}
                  sx={{ mt: 1 }}
                >
                  View Details
                </Button>
              </Box>
            </Box>
          </Paper>
        </Grid>
      </Grid>

      {/* Main dashboard items */}


      {/* Latest Research - Full Width */}
      <Paper elevation={3} sx={{ 
        p: { xs: 1.5, sm: 2, md: 3 },
        width: '100%',
        background: isDarkMode
          ? 'linear-gradient(120deg, rgba(40,50,40,1) 0%, rgba(30,45,40,1) 100%)'
          : 'linear-gradient(120deg, rgba(245,255,245,1) 0%, rgba(235,255,250,1) 100%)',
        borderRadius: 2
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <School color="primary" sx={{ mr: 1 }} />
          <Typography variant="h6" fontWeight="medium">Latest Research</Typography>
        </Box>
        <Grid container spacing={2}>
          {recentResearch.map((item, index) => (
            <Grid item xs={12} sm={4} key={index}>
              <Box sx={{ 
                display: 'flex', 
                flexDirection: 'column',
                height: '100%',
                p: 2,
                borderRadius: 2,
                bgcolor: isDarkMode 
                  ? alpha(theme.palette.background.paper, 0.15) 
                  : alpha(theme.palette.common.white, 0.9),
                '&:hover': { 
                  bgcolor: isDarkMode 
                    ? alpha(theme.palette.background.paper, 0.25) 
                    : alpha(theme.palette.common.white, 1),
                  boxShadow: `0 4px 8px ${alpha(theme.palette.common.black, 0.1)}`,
                },
                cursor: 'pointer',
                transition: 'all 0.2s ease-in-out'
              }} onClick={() => item.link && window.open(item.link, '_blank')}>
                <Typography variant="subtitle1" fontWeight="medium" gutterBottom>{item.title}</Typography>
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 'auto', pt: 1 }}>
                  <Chip 
                    label={item.tag} 
                    size="small" 
                    color={
                      item.tag === "AI" ? "primary" : 
                      item.tag === "ML" ? "secondary" : 
                      "default"
                    }
                    sx={{ height: 24 }}
                  />
                </Box>
              </Box>
            </Grid>
          ))}
        </Grid>
      </Paper>
    </Box>
  );
}